import React, { useState } from "react";
import { ShareApplicationPerson } from "@/types/ShareApplication";
import { PrimaryButton, ReadOnlyField } from "@ucl/library";
import { useLinkUser } from "../useLinkUser";
import "./UnlinkedWithPotentialUserComponent.scss";

export interface UnlinkedWithPotentialUserComponentProps {
  person: ShareApplicationPerson;
  financialApplicationId?: string;
  onUserLinked?: () => void;
}

export const UnlinkedWithPotentialUserComponent: React.FC<
  UnlinkedWithPotentialUserComponentProps
> = ({ person, financialApplicationId, onUserLinked }) => {
  const { linkUser, isLinking, error } = useLinkUser();
  const [isLinked, setIsLinked] = useState(false);

  const handleLinkUser = async () => {
    if (!financialApplicationId) {
      console.error("Financial application ID is required");
      return;
    }

    if (!person.potentialUserId) {
      console.error("No potential user ID found for this person");
      return;
    }

    try {
      await linkUser(financialApplicationId, person);
      setIsLinked(true);
      onUserLinked?.();
    } catch (err) {
      // Error is already handled in the hook
      console.error("Failed to link user:", err);
    }
  };

  return (
    <div className="unlinked-with-potential-user-component">
      <h3>Link Existing User Account</h3>
      <p>
        We found an existing user account with the email address {person.emailAddress}. You can link
        this person to their existing account instead of creating a new invite.
      </p>

      <div className="unlinked-with-potential-user-component__email-section">
        <ReadOnlyField
          value={person.emailAddress}
          label="Email Address"
          description="The email address of the existing user account"
          minimal={false}
        />
      </div>

      {error && (
        <div className="unlinked-with-potential-user-component__error">
          <p>Error: {error}</p>
        </div>
      )}

      {isLinked ? (
        <div className="unlinked-with-potential-user-component__success">
          <h4>User Linked Successfully!</h4>
          <p>The person has been linked to their existing user account.</p>
        </div>
      ) : (
        <div className="unlinked-with-potential-user-component__link-section">
          <PrimaryButton
            text={isLinking ? "Linking..." : "Link to Existing Account"}
            onClick={handleLinkUser}
            disabled={isLinking || !financialApplicationId || !person.potentialUserId}
          />
        </div>
      )}
    </div>
  );
};

export default UnlinkedWithPotentialUserComponent;
